import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { RefreshCw, DownloadCloud, TrendingUp, ChevronDown, MailPlus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { clientsAPI } from '../lib/clientsAPI';
import { importAPI, errText as importErrText } from '../lib/importAPI';
import api, { xeroAPI, xeroFinancialsAPI, FINANCIALS_ENABLED } from '../lib/api';
import { ClientListTable } from '../components/clients/ClientListTable';

export default function Clients() {
  const navigate = useNavigate();
  const [reloadKey, setReloadKey] = useState(0);
  const loadClients = async () => setReloadKey((k) => k + 1);
  const [syncingXero, setSyncingXero] = useState(false);
  const [syncingAllFin, setSyncingAllFin] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [userRole, setUserRole] = useState(undefined);
  const [selected, setSelected] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null); // client object pending deletion
  const [deleting, setDeleting] = useState(false);

  const canDelete = ['owner', 'admin'].includes(userRole);
  // RBAC Tier 4: mirror the backend capabilities. 'staff' is the legacy alias for 'accountant'.
  // create_edit_client (Send Invite) → owner/admin/accountant/bookkeeper; sync_xero → owner/admin/accountant.
  const canManage = ['owner', 'admin', 'accountant', 'bookkeeper', 'staff'].includes(userRole);
  const canSyncXero = ['owner', 'admin', 'accountant', 'staff'].includes(userRole);

  useEffect(() => {
    api.get('/api/me').then((res) => setUserRole(res.data.active_membership?.role)).catch(() => setUserRole(undefined));
  }, []);

  const bulkInvite = async () => {
    setInviting(true);
    try {
      const r = await importAPI.bulkInvite(selected);
      const skipped = r.results.filter((x) => x.result !== 'invited');
      toast.success(`${r.invited} invited${skipped.length ? `, ${skipped.length} skipped` : ''}`, {
        description: skipped.slice(0, 5).map((x) => `${x.name || x.client_id}: ${x.reason}`).join(' · ') || undefined,
      });
      setSelected([]);
    } catch (e) {
      toast.error(importErrText(e));
    } finally {
      setInviting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!clientToDelete) return;
    setDeleting(true);
    try {
      await clientsAPI.remove(clientToDelete.id);
      toast.success('Client deleted (and archived in Xero)');
      setClientToDelete(null);
      await loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreClient = async (client) => {
    try {
      await clientsAPI.update(client.id, { status: 'active' });
      toast.success(`${client.display_name} restored`);
      await loadClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore client');
    }
  };

  // full=false → incremental (only new/changed since last sync, fast). full=true → re-pull every contact.
  const handleXeroSync = async (full = false) => {
    setSyncingXero(true);
    toast.info(full ? 'Re-syncing all contacts from Xero…' : 'Syncing new & changed contacts…');
    try {
      const { data } = await xeroAPI.pull(full);
      const n = data.updated ?? data.processed ?? 0;
      toast.success(n === 0 ? 'Already up to date — no new or changed contacts.' : `Synced from Xero: ${n} contact${n === 1 ? '' : 's'} added/updated.`);
      await loadClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 404 || /no active xero/i.test(detail || '')) toast.error('Xero is not connected. Connect it in Settings → Integrations first.');
      else toast.error(detail || 'Could not sync from Xero');
    } finally {
      setSyncingXero(false);
    }
  };

  // Import financials for ONE client (row menu) → land on its Financials tab.
  const handleImportClientFinancials = async (client) => {
    setImportingId(client.id);
    toast.info(`Importing financials for ${client.display_name}…`);
    try {
      const { data } = await xeroFinancialsAPI.importForClient(client.id);
      toast.success(`Imported ${data.imported.length} reports from ${data.source}`);
      navigate(`/clients/${client.id}?tab=financials`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not import financials', { duration: 8000 });
    } finally {
      setImportingId(null);
    }
  };

  // Bulk: fire the background job, then poll status until it finishes.
  const handleSyncAllFinancials = async () => {
    setSyncingAllFin(true);
    try {
      await xeroFinancialsAPI.importAll();
      toast.info('Importing financials for all clients in the background…');
      const poll = setInterval(async () => {
        try {
          const { data } = await xeroFinancialsAPI.bulkStatus();
          if (data.status === 'completed') {
            clearInterval(poll);
            setSyncingAllFin(false);
            const errs = data.errors?.length || 0;
            toast.success(`Financials synced for ${data.done}/${data.total} clients${errs ? ` (${errs} failed)` : ''}.`);
          }
        } catch {
          clearInterval(poll);
          setSyncingAllFin(false);
        }
      }, 2000);
    } catch (error) {
      setSyncingAllFin(false);
      toast.error(error.response?.data?.detail || 'Could not start bulk import');
    }
  };

  return (
    <div className="space-y-5 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-page-title" data-testid="clients-title">Clients</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage your clients and onboarding</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canSyncXero && (
            <div className="flex">
              <Button variant="outline" onClick={() => handleXeroSync(false)} disabled={syncingXero} data-testid="xero-sync-button" title="Pull only new & changed contacts from Xero (fast)" className="rounded-r-none">
                <RefreshCw className={`mr-2 h-4 w-4 ${syncingXero ? 'animate-spin' : ''}`} />{syncingXero ? 'Syncing…' : 'Sync from Xero'}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={syncingXero} className="rounded-l-none border-l-0 px-2" data-testid="xero-sync-menu" aria-label="More Xero sync options"><ChevronDown className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleXeroSync(true)} data-testid="xero-full-resync"><RefreshCw className="mr-2 h-4 w-4" />Full re-sync (all contacts)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {canSyncXero && FINANCIALS_ENABLED && (
            <Button variant="outline" onClick={handleSyncAllFinancials} disabled={syncingAllFin} data-testid="sync-all-financials-button" title="Import financial reports from Xero for all clients">
              <TrendingUp className={`mr-2 h-4 w-4 ${syncingAllFin ? 'animate-pulse' : ''}`} />{syncingAllFin ? 'Syncing financials…' : 'Sync All Financials'}
            </Button>
          )}
          {canDelete && (
            <Button variant="outline" onClick={() => navigate('/clients/import')} data-testid="import-clients-button"><DownloadCloud className="mr-2 h-4 w-4" />Import clients (CSV)</Button>
          )}
          {canDelete && selected.length > 0 && (
            <Button variant="outline" onClick={bulkInvite} disabled={inviting} data-testid="bulk-invite-button"><MailPlus className="mr-2 h-4 w-4" />{inviting ? 'Inviting…' : `Invite to portal (${selected.length})`}</Button>
          )}
          {canManage && (
            <Button onClick={() => navigate('/new-clients/new')} data-testid="send-invite-button"><MailPlus className="mr-2 h-4 w-4" />Send Invite</Button>
          )}
        </div>
      </div>

      <ClientListTable canDelete={canDelete} canManage={canManage} selected={selected} setSelected={setSelected} reloadKey={reloadKey}
        onRestore={handleRestoreClient} onDelete={setClientToDelete} onImportFinancials={handleImportClientFinancials} importingId={importingId} />

      <Dialog open={!!clientToDelete} onOpenChange={(open) => !open && setClientToDelete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete client?</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will remove <span className="font-semibold">{clientToDelete?.display_name}</span> from your clients list. The data is kept (recoverable by an admin), and the matching contact in
              Xero will be <span className="font-semibold">archived</span> — not deleted.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setClientToDelete(null)} disabled={deleting}>Cancel</Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleting} data-testid="confirm-delete-client-list-btn">{deleting ? 'Deleting…' : 'Delete Client'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
